<template>
  <header class="site" :class="{ 'has-blessing-link': loggedIn }">
    <div class="wrap">
      <router-link class="brand" to="/" aria-label="The Dream International home"><LogoFull /></router-link>
      <nav class="main" :class="{ open: menuOpen }" id="nav" aria-label="Main">
        <router-link to="/about">About</router-link>
        <router-link to="/programs">Programs</router-link>
        <router-link to="/support">Get involved</router-link>
        <router-link to="/faq">FAQ</router-link>
        <router-link v-if="isAdmin" to="/admin" target="_blank">Admin</router-link>
        <!-- .head-actions .signin is hidden below 920px, so the account actions repeat here for the mobile menu. -->
        <template v-if="loggedIn">
          <router-link class="nav-account" :to="{ name: 'DreamersBlessing' }">Dreamers Blessing</router-link>
          <router-link class="nav-account" :to="{ name: 'StudentPage' }">My Page</router-link>
          <a class="nav-account" href="#" @click.prevent="logOut">Log out</a>
        </template>
        <a v-else class="nav-account" href="#" @click.prevent="openLogin()">Sign in</a>
      </nav>
      <div class="head-actions">
        <template v-if="loggedIn">
          <button class="signin" type="button" @click="$router.push({ name: 'DreamersBlessing' })">Dreamers Blessing</button>
          <button class="signin" type="button" @click="$router.push({ name: 'StudentPage' })">My Page</button>
          <button class="signin" type="button" @click="logOut">Log out</button>
        </template>
        <button v-else class="signin" type="button" @click="openLogin()">Sign in</button>
        <router-link class="btn btn-gold" to="/support#give">Give</router-link>
      </div>
      <button class="menu-btn" type="button" id="menuBtn" aria-controls="nav" :aria-expanded="menuOpen ? 'true' : 'false'" aria-label="Open menu" @click="menuOpen = !menuOpen">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
      </button>
    </div>
  </header>
  <LoginOverlay
      :showModal="showLoginModal"
      :start-with-create-account="startLoginWithCreateAccount"
      :redirect="loginRedirect"
      @close="closeLogin"
  ></LoginOverlay>
  <PopupOverlay
      v-if="showPopupModal && shouldShowPopup"
      :showModal="showPopupModal"
      @close="showPopupModal = false"
      @signup="openSignUpFromPopup"
  ></PopupOverlay>
</template>

<script>
import LogoFull from './LogoFull.vue';
import LoginOverlay from '@/components/overlays/loginOverlay.vue';
import PopupOverlay from '@/components/overlays/popupOverlay.vue';
import { auth } from '@/firebase.config';
import { onAuthStateChanged } from 'firebase/auth';
import { getUser } from '@/api';
import { OPEN_LOGIN_EVENT } from '@/composables/useLogin';

const ADMIN_ACCESS_CACHE_KEY = 'adminAccess';
const ADMIN_ROLES = ['admin', 'coordinator', 'viewer'];

export default {
  name: 'SiteHeader',
  components: { LogoFull, LoginOverlay, PopupOverlay },
  data: () => ({
    menuOpen: false,
    showLoginModal: false,
    startLoginWithCreateAccount: false,
    loginRedirect: null,
    showPopupModal: false,
    authUser: auth.currentUser,
    isAdmin: false,
    unsubscribeAuth: null,
  }),
  computed: {
    loggedIn() {
      return Boolean(this.$store.getters['authStore/user'] || this.authUser);
    },
    inviteCode() {
      return this.$route.query.code || new URLSearchParams(window.location.search).get('code');
    },
    isTeacherInviteLink() {
      return String(this.inviteCode || '').toLowerCase() === 'njonnuri';
    },
    shouldShowPopup() {
      return this.isTeacherInviteLink || !this.loggedIn;
    },
  },
  watch: {
    $route() {
      this.menuOpen = false;
    },
  },
  created() {
    if (this.isTeacherInviteLink) {
      this.showPopupModal = true;
      return;
    }
    if (!localStorage.getItem('hasVisited')) {
      // 처음 방문 → 팝업 보여줌
      this.showPopupModal = true;
      localStorage.setItem('hasVisited', 'true');
    }
  },
  mounted() {
    window.addEventListener(OPEN_LOGIN_EVENT, this.onOpenLoginEvent);
    this.unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      this.authUser = user;
      if (!user) {
        this.isAdmin = false;
        return;
      }
      this.showPopupModal = false;
      await this.loadUserAccess(user);
    });
  },
  beforeUnmount() {
    window.removeEventListener(OPEN_LOGIN_EVENT, this.onOpenLoginEvent);
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  },
  methods: {
    async loadUserAccess(authenticatedUser) {
      try {
        const [user, tokenResult] = await Promise.all([
          getUser(),
          authenticatedUser.getIdTokenResult(),
        ]);
        const cachedAccess = JSON.parse(localStorage.getItem(ADMIN_ACCESS_CACHE_KEY) || 'null');
        const cachedRole = cachedAccess?.uid === authenticatedUser.uid ? cachedAccess.role : '';
        const adminRole = [
          user?.adminRole,
          user?.admin_role,
          tokenResult?.claims?.adminRole,
          tokenResult?.claims?.admin_role,
          tokenResult?.claims?.role,
          cachedRole,
        ].find((role) => ADMIN_ROLES.includes(String(role || '').toLowerCase()));
        this.isAdmin = Boolean(adminRole);
      } catch (error) {
        console.error(error);
        this.isAdmin = false;
      }
    },
    onOpenLoginEvent(event) {
      this.openLogin(Boolean(event?.detail?.createAccount), event?.detail?.redirect || null);
    },
    openLogin(createAccount = false, redirect = null) {
      this.menuOpen = false;
      this.startLoginWithCreateAccount = createAccount;
      this.loginRedirect = redirect;
      this.showLoginModal = true;
    },
    closeLogin() {
      this.showLoginModal = false;
      this.startLoginWithCreateAccount = false;
      this.loginRedirect = null;
    },
    openSignUpFromPopup() {
      if (this.inviteCode) {
        localStorage.setItem('code', this.inviteCode);
      }
      if (this.isTeacherInviteLink) {
        localStorage.setItem('role', 'Teacher');
      }
      this.showPopupModal = false;
      this.openLogin(true);
    },
    logOut() {
      this.$store.dispatch('authStore/logOut').then(() => {
        localStorage.removeItem(ADMIN_ACCESS_CACHE_KEY);
        this.menuOpen = false;
        this.closeLogin();
        this.$router.replace({ name: 'Home' });
      }).catch((error) => {
        console.error(error);
      });
    },
  },
};
</script>

<style>
.tdi nav.main .nav-account { display: none; }
@media (max-width: 920px) {
  .tdi nav.main.open .nav-account { display: block; }
}
/* The signed-in Dreamers Blessing link would wrap to two lines between the
   menu breakpoint and ~1080px, so tighten the spacing there (measured to fit at 921px). */
@media (min-width: 921px) and (max-width: 1100px) {
  .tdi header.site.has-blessing-link .wrap { gap: 14px; }
  .tdi header.site.has-blessing-link nav.main { gap: 0; }
  .tdi header.site.has-blessing-link nav.main a { padding: 8px 7px; }
  .tdi header.site.has-blessing-link .head-actions { gap: 4px; }
  .tdi header.site.has-blessing-link .head-actions .signin { white-space: nowrap; padding: 8px 6px; }
}
</style>
